import {
  useQuery,
  useMutation,
  useQueryClient,
  type QueryClient,
  type UseQueryResult,
  type UseMutationResult,
} from '@/lib/query';
import {
  getDriverGroups,
  createDriverGroup,
  updateDriverGroup,
  deleteDriverGroup,
} from '@/modules/driver-groups/api';
import {
  CreateDriverGroupDto,
  DriverGroupDto,
  SuccessResponse,
  UpdateDriverGroupDto,
} from '@cargovibe/shared-types';

const DRIVER_GROUPS_QUERY_KEY: string[] = ['driver-groups'];

interface UpdateDriverGroupVariables {
  groupId: string;
  data: UpdateDriverGroupDto;
}

export const useDriverGroups = (): UseQueryResult<DriverGroupDto[]> => {
  return useQuery<DriverGroupDto[]>({
    queryKey: DRIVER_GROUPS_QUERY_KEY,
    queryFn: getDriverGroups,
  });
};

export const useCreateDriverGroup = (): UseMutationResult<
  DriverGroupDto,
  Error,
  CreateDriverGroupDto
> => {
  const queryClient: QueryClient = useQueryClient();

  return useMutation<DriverGroupDto, Error, CreateDriverGroupDto>({
    mutationFn: (data: CreateDriverGroupDto): Promise<DriverGroupDto> => createDriverGroup(data),
    onSuccess: (group: DriverGroupDto): void => {
      queryClient.setQueryData<DriverGroupDto[]>(
        DRIVER_GROUPS_QUERY_KEY,
        (previousGroups: DriverGroupDto[] | undefined): DriverGroupDto[] =>
          [...(previousGroups ?? []), group].sort(
            (groupA: DriverGroupDto, groupB: DriverGroupDto): number =>
              groupA.name.localeCompare(groupB.name)
          )
      );
      void queryClient.invalidateQueries({ queryKey: DRIVER_GROUPS_QUERY_KEY });
    },
  });
};

export const useUpdateDriverGroup = (): UseMutationResult<
  DriverGroupDto,
  Error,
  UpdateDriverGroupVariables
> => {
  const queryClient: QueryClient = useQueryClient();

  return useMutation<DriverGroupDto, Error, UpdateDriverGroupVariables>({
    mutationFn: ({ groupId, data }: UpdateDriverGroupVariables): Promise<DriverGroupDto> =>
      updateDriverGroup(groupId, data),
    onSuccess: (): void => {
      void queryClient.invalidateQueries({ queryKey: DRIVER_GROUPS_QUERY_KEY });
    },
  });
};

export const useDeleteDriverGroup = (): UseMutationResult<SuccessResponse, Error, string> => {
  const queryClient: QueryClient = useQueryClient();

  return useMutation<SuccessResponse, Error, string>({
    mutationFn: (groupId: string): Promise<SuccessResponse> => deleteDriverGroup(groupId),
    onSuccess: (): void => {
      void queryClient.invalidateQueries({ queryKey: DRIVER_GROUPS_QUERY_KEY });
    },
  });
};
